import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Chip, MenuItem, Skeleton, Typography } from "@mui/material";
import MedicationIcon from "@mui/icons-material/Medication";
import AddIcon from "@mui/icons-material/Add";
import LinkIcon from "@mui/icons-material/Link";

import {
  IPD_COLORS,
  IPD_SHAPE,
  getIpdSelectMenuProps,
  ipdElevatedInputSx,
  ipdLabelSx,
  IpdButton,
  IpdSectionCard,
  IpdTextField,
} from "../../ipd/utils";
import { formatDateTime } from "../constants";
import { listMedications, recordMedication } from "../api/emergencyApi";
import MedicinePriceDialog from "./MedicinePriceDialog";

/**
 * Medicines given during the visit (ER-MED-001…003).
 *
 * A log of what was actually put into the patient — the medicine, how much, by
 * what route, when, and who gave it — newest first. Deliberately not a dosing
 * schedule (ER-MED-002): no due time, no missed or held dose, nothing about a
 * next dose. Emergency stays are measured in hours; that machinery belongs to
 * inpatient care, and the card's wording says so.
 *
 * Fetches its own list rather than reading it off the visit, so it carries a
 * real loading state and refreshes after a dose without reloading the whole
 * visit. The medicine orders already on the visit are offered as the
 * prescription a dose can be recorded against (ER-MED-003) — or none at all,
 * for a dose given without a matching prescription (ER-MED-001).
 */

const blankForm = () => ({
  medicine_name: "",
  dose: "",
  route: "",
  order_id: "",
  note: "",
  // The pharmacy item the dose came from, which charges it. Optional: a dose
  // recorded without one waits in the Charges tab's charge check for an item.
  stock: null,
});

export default function MedicationsCard({ visit, disabled, onRecorded }) {
  const visitId = visit.id;
  const medicineOrders = (visit.orders || []).filter(
    (o) => o.order_type === "medicine"
  );

  const [doses, setDoses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const [pricing, setPricing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setDoses(await listMedications(visitId));
      setLoadError("");
    } catch (e) {
      setLoadError(e?.message || "Could not load the medicines given");
    } finally {
      setLoading(false);
    }
  }, [visitId]);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (patch) => setForm((f) => ({ ...f, ...patch }));
  const ready = Boolean(
    form.medicine_name.trim() && form.dose.trim() && form.route.trim()
  );

  const submit = async () => {
    setBusy(true);
    setFormError("");
    try {
      const { message, chargeWarning } = await recordMedication(visitId, {
        medicineName: form.medicine_name.trim(),
        dose: form.dose.trim(),
        route: form.route.trim(),
        orderId: form.order_id || undefined,
        note: form.note.trim() || undefined,
        stock: form.stock
          ? {
              itemId: form.stock.itemId,
              itemName: form.stock.itemName,
              batchNumber: form.stock.batchNumber,
              quantity: form.stock.quantity,
            }
          : undefined,
      });
      setForm(blankForm());
      setAdding(false);
      await load();
      onRecorded?.(chargeWarning || message);
    } catch (e) {
      setFormError(e?.message || "Could not record the dose");
    } finally {
      setBusy(false);
    }
  };

  return (
    <IpdSectionCard
      title="Medicines given"
      subtitle="What was actually administered — a record, not a schedule"
      actions={
        !disabled &&
        !adding && (
          <IpdButton
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              setForm(blankForm());
              setFormError("");
              setAdding(true);
            }}
          >
            Record dose
          </IpdButton>
        )
      }
    >
      {/* ---------------------------------------------------------- the form */}
      {adding && (
        <Box
          sx={{
            mb: 2,
            p: 1.75,
            borderRadius: `${IPD_SHAPE.radiusMd}px`,
            border: `1px solid ${IPD_COLORS.border}`,
            bgcolor: IPD_COLORS.cardBg,
          }}
        >
          {formError && (
            <Alert
              severity="error"
              sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}
              onClose={() => setFormError("")}
            >
              {formError}
            </Alert>
          )}

          <Box sx={{ display: "grid", gap: 1.75 }}>
            <Box>
              <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>Medicine</Typography>
              <IpdTextField
                value={form.medicine_name}
                onChange={(e) => setField({ medicine_name: e.target.value })}
                placeholder="Medicine and strength"
                size="small"
                fullWidth
                autoFocus
                sx={ipdElevatedInputSx}
                inputProps={{ "aria-label": "Medicine" }}
              />
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1.5,
              }}
            >
              <Box>
                <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>Dose</Typography>
                <IpdTextField
                  value={form.dose}
                  onChange={(e) => setField({ dose: e.target.value })}
                  placeholder="e.g. 500 mg"
                  size="small"
                  fullWidth
                  sx={ipdElevatedInputSx}
                  inputProps={{ "aria-label": "Dose" }}
                />
              </Box>
              <Box>
                <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>Route</Typography>
                <IpdTextField
                  value={form.route}
                  onChange={(e) => setField({ route: e.target.value })}
                  placeholder="e.g. IV, oral, IM"
                  size="small"
                  fullWidth
                  sx={ipdElevatedInputSx}
                  inputProps={{ "aria-label": "Route" }}
                />
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <IpdButton
                variant="outlined"
                size="small"
                // One snapshot of the dose for the picker. A dose object rebuilt
                // on every render reset the picker, which then dropped its search
                // results and read "No pharmacy item matches".
                onClick={() =>
                  setPricing({
                    medicine_name: form.medicine_name.trim(),
                    dose: form.dose.trim(),
                    route: form.route.trim(),
                    administered_at: new Date().toISOString(),
                  })
                }
                disabled={!form.medicine_name.trim()}
              >
                {form.stock ? "Change stocked item" : "Choose stocked item"}
              </IpdButton>
              <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary }}>
                {form.stock
                  ? `${form.stock.itemName} · batch ${form.stock.batchNumber} · ${form.stock.quantity} ${
                      form.stock.uom || "unit(s)"
                    }`
                  : "Charges the dose from pharmacy stock (optional)"}
              </Typography>
            </Box>

            {medicineOrders.length > 0 && (
              <Box>
                <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>
                  Against a prescription (optional)
                </Typography>
                <IpdTextField
                  select
                  value={form.order_id}
                  onChange={(e) => setField({ order_id: e.target.value })}
                  size="small"
                  fullWidth
                  sx={ipdElevatedInputSx}
                  SelectProps={{ MenuProps: getIpdSelectMenuProps() }}
                  inputProps={{ "aria-label": "Against a prescription" }}
                >
                  <MenuItem value="">Not against a prescription</MenuItem>
                  {medicineOrders.map((o) => (
                    <MenuItem key={o.id} value={o.id}>
                      {o.item_name}
                    </MenuItem>
                  ))}
                </IpdTextField>
              </Box>
            )}

            <Box>
              <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>
                Note (optional)
              </Typography>
              <IpdTextField
                value={form.note}
                onChange={(e) => setField({ note: e.target.value })}
                placeholder="Anything worth recording about this dose"
                size="small"
                fullWidth
                sx={ipdElevatedInputSx}
                inputProps={{ "aria-label": "Note" }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              mt: 1.75,
            }}
          >
            <IpdButton
              variant="outlined"
              onClick={() => {
                setAdding(false);
                setFormError("");
              }}
              disabled={busy}
            >
              Cancel
            </IpdButton>
            <IpdButton onClick={submit} loading={busy} disabled={!ready}>
              Record dose
            </IpdButton>
          </Box>
        </Box>
      )}

      <MedicinePriceDialog
        open={Boolean(pricing)}
        visitId={visitId}
        // The dose is being given now; the dialog shows it in its heading.
        dose={pricing}
        onClose={() => setPricing(null)}
        onChoose={(choice) => setField({ stock: choice })}
      />

      {/* ---------------------------------------------------------- the list */}
      {loading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {[0, 1].map((i) => (
            <Skeleton
              key={i}
              variant="rounded"
              height={58}
              sx={{ borderRadius: `${IPD_SHAPE.radiusMd}px` }}
            />
          ))}
        </Box>
      ) : loadError ? (
        <Alert
          severity="error"
          sx={{ borderRadius: `${IPD_SHAPE.radiusMd}px` }}
          action={
            <IpdButton variant="outlined" size="small" onClick={load}>
              Retry
            </IpdButton>
          }
        >
          {loadError}
        </Alert>
      ) : doses.length === 0 ? (
        !adding && (
          <Box
            sx={{
              textAlign: "center",
              py: 3,
              px: 2,
              border: `1px dashed ${IPD_COLORS.borderHover}`,
              borderRadius: `${IPD_SHAPE.radiusMd}px`,
            }}
          >
            <MedicationIcon sx={{ fontSize: 26, color: IPD_COLORS.textMuted }} />
            <Typography sx={{ mt: 1, fontSize: 14, fontWeight: 700 }}>
              No medicines recorded yet
            </Typography>
            <Typography
              sx={{ mt: 0.5, fontSize: 13, color: IPD_COLORS.textSecondary }}
            >
              {disabled
                ? "No medicines were recorded as given on this visit."
                : "Each dose given in the department is written down here — what it was, when, and who gave it."}
            </Typography>
          </Box>
        )
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {doses.map((d) => (
            <Box
              key={d.id}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.25,
                px: 1.5,
                py: 1.25,
                borderRadius: `${IPD_SHAPE.radiusMd}px`,
                border: `1px solid ${IPD_COLORS.border}`,
                bgcolor: IPD_COLORS.cardBg,
              }}
            >
              <MedicationIcon
                sx={{ fontSize: 18, mt: 0.2, color: IPD_COLORS.primary }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "baseline",
                    flexWrap: "wrap",
                    gap: 0.75,
                  }}
                >
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
                    {d.medicine_name}
                  </Typography>
                  <Typography
                    sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary }}
                  >
                    {d.dose} · {d.route}
                  </Typography>
                </Box>
                <Typography
                  sx={{ fontSize: 11.5, color: IPD_COLORS.textMuted, mt: 0.25 }}
                >
                  {formatDateTime(d.administered_at)}
                  {d.administered_by_name ? ` · ${d.administered_by_name}` : ""}
                </Typography>
                {d.order_item_name && (
                  <Chip
                    size="small"
                    icon={<LinkIcon sx={{ fontSize: 13 }} />}
                    label={`Against: ${d.order_item_name}`}
                    sx={{
                      mt: 0.6,
                      height: 20,
                      fontSize: 11,
                      fontWeight: 600,
                      color: IPD_COLORS.primary,
                      bgcolor: "transparent",
                      border: `1px solid ${IPD_COLORS.borderHover}`,
                      "& .MuiChip-icon": { color: IPD_COLORS.primary, ml: 0.5 },
                    }}
                  />
                )}
                {d.note && (
                  <Typography
                    sx={{ fontSize: 12.5, color: IPD_COLORS.textPrimary, mt: 0.4 }}
                  >
                    {d.note}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </IpdSectionCard>
  );
}
